<template>
  <div v-if="canApprove" class="approval-action-buttons">
    <el-button type="success" @click="handleAction(true)" :loading="submitting">
      {{ isAgreementStep ? '동의' : '승인' }}
    </el-button>
    <el-button type="danger" @click="handleAction(false)" :loading="submitting">
      반려
    </el-button>

    <!-- 코멘트 다이얼로그 -->
    <el-dialog v-model="showDialog" title="결재 처리" width="400px" append-to-body>
      <el-form :model="form">
        <el-form-item label="코멘트">
          <el-input v-model="form.comments" type="textarea" :rows="3" placeholder="코멘트 입력 (선택)" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showDialog = false">취소</el-button>
        <el-button
          :type="form.isApproved ? 'success' : 'danger'"
          @click="submitAction"
          :loading="submitting"
        >
          {{ form.isApproved ? (isAgreementStep ? '동의' : '승인') : '반려' }}
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed } from 'vue'
import { useUserStore } from '@/stores/user'
import { ElMessage } from 'element-plus'
import axiosInstance from '@/api/axios'

const props = defineProps({
  approvalRequest: { type: Object, default: null },
})

const emit = defineEmits(['refresh'])
const userStore = useUserStore()

const showDialog = ref(false)
const submitting = ref(false)
const form = reactive({ isApproved: true, comments: '' })

// 내가 처리할 수 있는 단계 찾기
const myProcessableStep = computed(() => {
  if (!props.approvalRequest || props.approvalRequest.status !== 'PENDING') return null
  const userId = userStore.profile?.id
  if (!userId) return null

  const steps = props.approvalRequest.steps || []

  // 1) APPROVAL: 순차 → 현재 대기 중인 첫 번째 결재 단계가 내 것인지
  const approvalSteps = steps.filter(s => !s.step_type || s.step_type === 'APPROVAL')
  const currentApproval = approvalSteps.find(s => s.status === 'PENDING')
  if (currentApproval && currentApproval.approver === userId) return currentApproval

  // 2) AGREEMENT: 병렬 → 내가 대기 중인 합의 단계
  const myAgreement = steps.find(
    s => s.step_type === 'AGREEMENT' && s.status === 'PENDING' && s.approver === userId
  )
  if (myAgreement) return myAgreement

  return null
})

const canApprove = computed(() => !!myProcessableStep.value)
const isAgreementStep = computed(() => myProcessableStep.value?.step_type === 'AGREEMENT')

const handleAction = (isApproved) => {
  form.isApproved = isApproved
  form.comments = ''
  showDialog.value = true
}

const submitAction = async () => {
  submitting.value = true
  try {
    const endpoint = form.isApproved ? 'approve' : 'reject'
    await axiosInstance.post(`approvals/${props.approvalRequest.id}/${endpoint}/`, {
      is_approved: form.isApproved,
      comments: form.comments,
    })
    ElMessage.success(form.isApproved ? '승인되었습니다.' : '반려되었습니다.')
    showDialog.value = false
    emit('refresh')
  } catch (error) {
    console.error('Approval action failed:', error)
    ElMessage.error(error.response?.data?.error || '처리 중 오류가 발생했습니다.')
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.approval-action-buttons {
  display: inline-flex;
  gap: 8px;
}
</style>
